/****************************************************
 * Module Name:  PricePlanDetailModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useEffect, useState } from "react";
import { Box, Tooltip, Button } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Utility from "../../../utility/utility";
import Constants from "../../../utility/constants";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import NivodaProxy from "../../../utility/proxy/manager/nivodaProxy";
import renderCellExpand from "../../common/dataGridCellExpand";
import Modal from "react-bootstrap/Modal";
import logo from '../../../images/pansuriya.jpg'

const PricePlanDetailModal = ({
    stoneId, kapanid, packetid, show, handleClose
}) => {
    const [listData, setListData] = useState([]);
    const [listLoader, setListLoader] = useState(false);
    const [innerFilterValues, setInnerFilterValues] = useState({});

    useEffect(() => {
        handleSearch(stoneId);
    }, []);

    const handleSearch = (stoneId) => {
        setListLoader(true);
        NivodaProxy.GetNivodaStockByStoneId(stoneId, (response) => {
            if (response) {
                const uniqueFilterValues = {
                    shape: Utility.getDistinctOptions(response, "shape"),
                    color: Utility.getDistinctOptions(response, "color"),
                    cut: Utility.getDistinctOptions(response, "cut"),
                    pol: Utility.getDistinctOptions(response, "pol"),
                    sym: Utility.getDistinctOptions(response, "sym"),
                    clarity: Utility.getDistinctOptions(response, "clarity"),
                    flo: Utility.getDistinctOptions(response, "flo"),
                    stonetype: Utility.getDistinctOptions(response, "stonetype"),
                    status: Utility.getDistinctOptions(response, "status"),
                    location: Utility.getDistinctOptions(response, "location"),
                };
                let position = 1;
                let responseData = response
                    .map(iResponse => ({ ...iResponse, diamondprice: Math.round(iResponse.diamondprice) }))
                    .sort((a, b) => a.diamondprice - b.diamondprice)
                    .map((iData, index, array) => {
                        if (index === 0 || iData.diamondprice === array[index - 1].diamondprice) {
                            iData.position = position;
                        } else {
                            iData.position = ++position;
                        }
                        return iData;
                    });
                setInnerFilterValues(uniqueFilterValues);
                setListData(responseData);
                setListLoader(false);
            }
        });

    };

    const getInnerColumns = () => [
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'shape',
            headerName: 'SHAPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'carat',
            headerName: 'SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'color',
            headerName: 'COLOR',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 65
        },
        {
            field: 'clarity',
            headerName: 'CLARITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 75
        },
        {
            field: 'cut',
            headerName: 'CUT',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'pol',
            headerName: 'POL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'sym',
            headerName: 'SYM',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'flo',
            headerName: 'FLO',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'stonetype',
            headerName: 'STONE TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueGetter: (params) => params.value === "L" ? "Lab Grown Diamonds" : "Natural Diamonds",
            valueOptions: innerFilterValues ? innerFilterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 150
        },
        {
            field: 'discount',
            headerName: 'DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 80,
            valueFormatter: (params) => params.value,
        },
        {
            field: "priceperct",
            headerName: "PPC",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 60,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "diamondprice",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 110,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "location",
            headerName: "LOC",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.location : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "position",
            headerName: "POSITION",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 100,
            renderCell: renderCellExpand
        },
    ];

    const columns = getInnerColumns();

    return (

        <div>
            <Modal
                enforceFocus={false}
                show={show}
                onHide={handleClose}
                dialogClassName="max-price-modalSize"
                style={{ zIndex: 1050 }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;
                        Pricing Details (Kapan ID - {kapanid} | Packet ID - {packetid} | Price Plan ID - {stoneId})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Box
                        sx={{
                            px: 1,
                            width: "100%",
                            position: "relative",
                            backgroundColor: '#fff',
                            color: '#000',
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                color: "#1976d2",
                                padding: "4px 5px",
                                borderRadius: "4px",
                            },
                            "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                color: "#1976d2",
                                padding: "5px 5px",
                                borderRadius: "4px",
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "65vh",
                        }}
                    >
                        <DataGridPro
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "6px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            disableRowSelectionOnClick={true}
                            slots={{ toolbar: GridToolbar }}
                            rows={listData}
                            columns={columns}
                            getRowId={(row) => row.id}
                            loading={listLoader}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        id: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: {
                                        debounceMs: 500,
                                    },
                                },
                            }}
                        />
                        <Box
                            style={{
                                position: "absolute",
                                top: 5,
                                right: "270px",
                            }}
                        >
                            {listLoader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => handleSearch(stoneId)}
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer", mr: 1 }}
                                    />
                                </Tooltip>
                            )}

                        </Box>
                    </Box>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );

};

export default PricePlanDetailModal;